import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

// MUI
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import PauseCircleOutlineRounded from '@mui/icons-material/PauseCircleOutlineRounded';
import PlayCircleOutlineRounded from '@mui/icons-material/PlayCircleOutlineRounded';

// common
import { ROUTES_PATH } from 'layout/constants';
import MuiTable from 'common/components/MuiTable';
import IconButtonTooltip from 'common/components/IconButtonTooltip';
import useUserRoles from 'common/hook/useUserRoles';

// screen
import useGetConfirmationCodes from 'modules/confirmation-code/services/useGetConfirmationCodes';
import useUpdateConfirmationCode from 'modules/confirmation-code/services/useUpdateConfirmationCode';
import useConfirmationClients from 'modules/confirmation-code/services/useConfirmationClients';
import { CONFIRMATION_CODE_STATUS, type ConfirmationCode } from 'modules/confirmation-code/types';

const fmt = (s?: string | null) => (s ? new Date(s).toLocaleDateString('vi-VN') : '');

const ConfirmationCodeListPage = () => {
  const navigate = useNavigate();
  const { nameOf } = useConfirmationClients();
  const { mutateAsync: updateCode } = useUpdateConfirmationCode();
  /**
   * Chỉ CS-BAD được tạo/sửa mã (xlsx 28.07 sheet 02 dòng 21: "Chỉ CS-BAD tạo"), khớp grant
   * BE trong confirmation_code_setup.sql (CS-BAD: list/read/create/write; Nutri-BAD + SAD:
   * chỉ list/read). Vai trò khác chỉ XEM — ẩn nút để không bấm vào rồi nhận 403.
   */
  const { isCSBAD: canWrite } = useUserRoles();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  // Bộ lọc mở trong Popover (đúng Figma + giống các màn danh sách khác của app).
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);

  const {
    data: codes = [],
    isLoading,
    isFetching,
    refetch,
  } = useGetConfirmationCodes({ status: status || undefined });

  // Tìm kiếm client-side theo tên trường học + mã code (đúng placeholder Figma).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return codes;
    return codes.filter(
      (c) =>
        c.code.toLowerCase().includes(q) || nameOf(c.targetOrgId).toLowerCase().includes(q)
    );
  }, [codes, search, nameOf]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * perPage, page * perPage),
    [filtered, page, perPage]
  );

  const applySearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const toggleStatus = async (c: ConfirmationCode) => {
    const next =
      c.status === CONFIRMATION_CODE_STATUS.ACTIVE
        ? CONFIRMATION_CODE_STATUS.DISABLED
        : CONFIRMATION_CODE_STATUS.ACTIVE;
    await updateCode({ id: c.id, body: { status: next } });
    refetch();
  };

  return (
    <Paper className="paper-list">
      {/* Hàng 1 (Figma): "Tìm kiếm:" + ô nhập + nút Tìm kiếm + Bộ lọc + Làm mới */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          pt: 2,
        }}
      >
        <Typography>Tìm kiếm:</Typography>
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          placeholder="Tìm kiếm theo tên trường học, mã code"
          // Figma: ô tìm kiếm rộng cố định (~490px), KHÔNG kéo giãn hết hàng.
          sx={{ width: 490, maxWidth: '100%' }}
        />
        <Button variant="contained" color="primary" startIcon={<SearchRounded />} onClick={applySearch}>
          Tìm kiếm
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={
            <Badge badgeContent={status ? 1 : 0} variant="standard" color="primary">
              <TuneOutlined />
            </Badge>
          }
          endIcon={<ExpandMoreOutlined />}
          onClick={(e) => setFilterAnchor(filterAnchor ? null : e.currentTarget)}
        >
          Bộ lọc
        </Button>
        <Button variant="outlined" color="primary" title="Làm mới" onClick={() => refetch()}>
          <RefreshRounded />
        </Button>
      </Box>

      {/* Hàng 2 (Figma): "Thêm mới" nằm riêng, canh phải — chỉ CS-BAD */}
      {canWrite && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1.5 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddRounded />}
            onClick={() => navigate(ROUTES_PATH.confirmationCode.create)}
          >
            Thêm mới
          </Button>
        </Box>
      )}

      <Popover
        open={!!filterAnchor}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            variant: 'outlined',
            elevation: 0,
            sx: { borderColor: 'primary.main', p: 2, mt: 2, width: 280 },
          },
        }}
      >
        <TextField
          select
          fullWidth
          size="small"
          label="Trạng thái quản trị"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">Tất cả</MenuItem>
          <MenuItem value={CONFIRMATION_CODE_STATUS.ACTIVE}>Hoạt động</MenuItem>
          <MenuItem value={CONFIRMATION_CODE_STATUS.DISABLED}>Tạm ngừng</MenuItem>
        </TextField>
      </Popover>

      <MuiTable<ConfirmationCode>
        data={paged}
        state={{ isLoading: isLoading || isFetching }}
        paginationProps={{
          count: filtered.length,
          page,
          rowsPerPage: perPage,
          onPageChange: setPage,
          onRowsPerPageChange: (v: number) => {
            setPerPage(v);
            setPage(1);
          },
        }}
        columns={[
          {
            accessorKey: 'rowNumber',
            header: 'STT',
            Header: () => 'STT',
            size: 60,
            enableSorting: false,
            Cell: ({ row }) => (page - 1) * perPage + row.index + 1,
          },
          {
            accessorKey: 'code',
            header: 'code',
            Header: () => 'Mã code',
            Cell: ({ renderedCellValue }) => (
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {`${renderedCellValue ?? ''}`}
              </Box>
            ),
          },
          {
            accessorKey: 'targetOrgId',
            header: 'targetOrgId',
            Header: () => 'Tên trường học',
            Cell: ({ row: { original } }) => nameOf(original.targetOrgId),
          },
          {
            accessorKey: 'expiredAt',
            header: 'expiredAt',
            Header: () => 'Hạn sử dụng',
            Cell: ({ renderedCellValue }) => fmt(`${renderedCellValue ?? ''}`),
          },
          {
            id: 'usage',
            header: 'usage',
            enableSorting: false,
            Header: () => 'Số lượt truy cập',
            Cell: ({ row: { original } }) => `${original.accessCount}/${original.accessLimit}`,
          },
          {
            accessorKey: 'description',
            header: 'description',
            Header: () => 'Ghi chú',
            Cell: ({ renderedCellValue }) => `${renderedCellValue ?? ''}`,
          },
          {
            accessorKey: 'status',
            header: 'status',
            Header: () => 'Trạng thái quản trị',
            Cell: ({ renderedCellValue }) => {
              const active = renderedCellValue === CONFIRMATION_CODE_STATUS.ACTIVE;
              return (
                <Chip
                  label={active ? 'Hoạt động' : 'Tạm ngừng'}
                  color={active ? 'success' : 'default'}
                  size="small"
                />
              );
            },
          },
        ]}
        enableRowActions
        renderRowActions={({ row: { original } }) => {
          if (!canWrite) return []; // Nutri-BAD/SAD/RAD: chỉ List/Read
          const active = original.status === CONFIRMATION_CODE_STATUS.ACTIVE;
          // Mã đã dùng hết lượt thì chỉ còn nút Sửa (Figma: hàng 1500/1500 không có nút
          // tạm ngừng/kích hoạt) — bật/tắt lúc này vô nghĩa vì mã không dùng được nữa;
          // muốn dùng lại phải sửa tăng hạn mức.
          const exhausted = original.accessCount >= original.accessLimit;
          return [
            <IconButtonTooltip
              key="edit"
              tooltipProps={{ title: 'Sửa' }}
              onClick={() => navigate(`${original.id}`)}
            >
              <EditRounded />
            </IconButtonTooltip>,
            ...(exhausted
              ? []
              : [
                  <IconButtonTooltip
                    key="toggle"
                    tooltipProps={{ title: active ? 'Tạm ngừng' : 'Kích hoạt' }}
                    onClick={() => toggleStatus(original)}
                  >
                    {active ? <PauseCircleOutlineRounded /> : <PlayCircleOutlineRounded />}
                  </IconButtonTooltip>,
                ]),
          ];
        }}
      />
    </Paper>
  );
};

export default ConfirmationCodeListPage;
